import { useTranslation } from 'react-i18next';
import type { ImportPreviewRow } from '@/api/clientImports/clientImportsApi.types';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CellIssueTooltip } from './CellIssueTooltip';
import {
  resolveIssuesForCell,
  resolveRowLevelIssues,
  resolveWorstSeverityForIssues,
} from './resolveCellSeverity';

interface ImportPreviewGridProps {
  columns: string[];
  rows: ImportPreviewRow[];
}

const CELL_SEVERITY_CLASS_NAME: Record<'error' | 'warning' | 'clean', string> = {
  error: 'bg-zooerrorbg text-zooerrortext',
  warning: 'bg-zoowarningbg text-zoowarningtext border-l-4 border-zoowarningtext',
  clean: 'bg-zoowhite text-zooindigo',
};

export function ImportPreviewGrid({ columns, rows }: ImportPreviewGridProps) {
  const { t } = useTranslation('clientImport');
  const dataRows = rows.filter(function isDataRow(row) {
    return row.rowNumber > 0;
  });
  const rowsWithIssues = dataRows.filter(function hasIssues(row) {
    return row.issues.length > 0;
  });

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-20">
              <tr className="border-b border-zooborder bg-zoooffwhite">
                <th
                  scope="col"
                  className="sticky left-0 z-30 border-r border-zooborder bg-zoooffwhite px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-zoomuted"
                >
                  {t('preview.rowNumberHeader')}
                </th>
                {columns.map(function renderHeader(column) {
                  return (
                    <th
                      key={column}
                      scope="col"
                      className="whitespace-nowrap bg-zoooffwhite px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-zoomuted"
                    >
                      {t('columns.' + column)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-zooborder">
              {dataRows.map(function renderRow(row) {
                const rowLevelIssues = resolveRowLevelIssues(row.issues);
                const rowSeverity = resolveWorstSeverityForIssues(rowLevelIssues);
                return (
                  <tr key={row.rowNumber}>
                    <th
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 whitespace-nowrap border-r border-zooborder px-3 py-2 font-bold',
                        CELL_SEVERITY_CLASS_NAME[rowSeverity === null ? 'clean' : rowSeverity]
                      )}
                    >
                      <CellIssueTooltip issues={rowLevelIssues}>
                        <span>{row.rowNumber}</span>
                      </CellIssueTooltip>
                    </th>
                    {columns.map(function renderCell(column) {
                      const cellIssues = resolveIssuesForCell(row.issues, column);
                      const cellSeverity = resolveWorstSeverityForIssues(cellIssues);
                      return (
                        <td
                          key={column}
                          className={cn(
                            'max-w-[220px] truncate whitespace-nowrap px-3 py-2',
                            CELL_SEVERITY_CLASS_NAME[cellSeverity === null ? 'clean' : cellSeverity]
                          )}
                        >
                          <CellIssueTooltip issues={cellIssues}>
                            <span>{row.cells[column]}</span>
                          </CellIssueTooltip>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {rowsWithIssues.length === 0 ? null : (
        <Card padding="sm" className="lg:hidden">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-zoomuted">
            {t('preview.issuesTitle')}
          </p>
          <div className="flex flex-col gap-3">
            {rowsWithIssues.map(function renderRowIssues(row) {
              return (
                <div key={row.rowNumber} className="border-l-4 border-zooborder pl-3">
                  <p className="text-sm font-bold text-zooindigo">
                    {t('preview.rowIssueLabel', { number: row.rowNumber })}
                  </p>
                  <ul className="mt-1 flex flex-col gap-1">
                    {row.issues.map(function renderIssue(issue, index) {
                      const columnLabel = issue.column === null ? '' : t('columns.' + issue.column) + ': ';
                      return (
                        <li
                          key={index}
                          className={cn(
                            'text-[13px]',
                            issue.severity === 'error' ? 'text-zooerrortext' : 'text-zoowarningtext'
                          )}
                        >
                          {columnLabel}
                          {issue.message}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}
